import { Link } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { useProfileUrl } from '@/hooks/useProfileUrl';
import { getDisplayName } from '@/lib/getDisplayName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { cn } from '@/lib/utils';

interface ProfileSnippetProps {
  pubkey: string;
  className?: string;
  avatarClassName?: string;
}

/** Avatar + display name linking to the user's profile page. */
export function ProfileSnippet({ pubkey, className, avatarClassName }: ProfileSnippetProps) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const profileUrl = useProfileUrl(pubkey, metadata);

  const name = getDisplayName(metadata, pubkey);
  const picture = sanitizeUrl(metadata?.picture);

  return (
    <Link
      to={profileUrl}
      className={cn(
        'group inline-flex min-w-0 items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <Avatar className={cn('size-8 border border-border', avatarClassName)}>
        {picture && <AvatarImage src={picture} alt={name} loading="lazy" />}
        <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-semibold">
          {name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="truncate font-medium text-sm group-hover:underline underline-offset-4 decoration-primary/60">
        {name}
      </span>
    </Link>
  );
}
